"use client";

import { useEffect, useRef, useState } from "react";
import { Badge } from "@/components/dubai/ui/badge";
import { Button } from "@/components/dubai/ui/button";

// Scroll-driven marquee (lg and up; below that dubai.css keeps the looping
// animation). The phrase drifts left as the page scrolls down and back right on
// the way up, rather than running on its own clock.
const MARQUEE_DRIFT = 0.35; // px the phrase travels per px of page scroll
const MARQUEE_SMOOTHING = 0.12; // how much of the remaining gap to close per frame
const MARQUEE_SETTLED = 0.05; // px below which the drift is treated as arrived

const requirements = [
  "Passport copy",
  "Digital passport photo",
  "Address details",
  "Emirates ID & visa page for UAE residents",
  "Corporate documents for entity setup",
];

export const CompanySetupRequirementsSection = () => {
  const marqueeRef = useRef<HTMLDivElement>(null);
  const [selectedRequirements, setSelectedRequirements] = useState<string[]>(
    [],
  );

  useEffect(() => {
    const track = marqueeRef.current;
    if (!track) return;

    const isDesktop = window.matchMedia("(min-width: 1024px)");
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );

    let frame = 0;
    let active = false;
    let offset = 0;

    const step = () => {
      frame = 0;
      // The track holds two copies of the phrase, so wrapping at half its width
      // lands on an identical frame — seamless in either scroll direction.
      const loopWidth = track.scrollWidth / 2;
      const target = window.scrollY * MARQUEE_DRIFT;
      const remaining = target - offset;

      offset =
        Math.abs(remaining) < MARQUEE_SETTLED
          ? target
          : offset + remaining * MARQUEE_SMOOTHING;

      const wrapped =
        loopWidth > 0 ? ((offset % loopWidth) + loopWidth) % loopWidth : 0;
      track.style.transform = `translate3d(${-wrapped}px, 0, 0)`;

      // Idle once the drift has caught up; the scroll listener wakes it again.
      if (offset !== target) frame = window.requestAnimationFrame(step);
    };

    const schedule = () => {
      if (active && !frame) frame = window.requestAnimationFrame(step);
    };

    const sync = () => {
      if (frame) window.cancelAnimationFrame(frame);
      frame = 0;
      active = isDesktop.matches && !prefersReducedMotion.matches;

      if (!active) {
        // Hand the phrase back to the CSS keyframe animation.
        track.style.transform = "";
        return;
      }

      offset = window.scrollY * MARQUEE_DRIFT; // adopt the current scroll, no lurch
      schedule();
    };

    sync();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    isDesktop.addEventListener("change", sync);
    prefersReducedMotion.addEventListener("change", sync);

    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      isDesktop.removeEventListener("change", sync);
      prefersReducedMotion.removeEventListener("change", sync);
    };
  }, []);

  const toggleRequirement = (requirement: string) => {
    setSelectedRequirements((current) =>
      current.includes(requirement)
        ? current.filter((item) => item !== requirement)
        : [...current, requirement],
    );
  };

  return (
    <section className="relative isolate flex min-h-screen w-full items-center overflow-y-hidden justify-center overflow-x-hidden bg-black px-6 py-12 sm:px-10 lg:px-[clamp(2.5rem,6.95vw,100px)]">
      <div className="pointer-events-none absolute left-[calc(50%_-_402px)] top-[-340px] -z-10 h-[319px] w-[1038px] rounded-full bg-[#0061FE] blur-[152px]" />
      <div className="pointer-events-none absolute right-[-18%] top-[120px] -z-10 h-[710px] w-[min(731px,70vw)] rotate-90 rounded-[336px] bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,97,254,0.2)_0%,rgba(0,97,254,0)_68%)] blur-[12.5px]" />

      <div className="mx-auto flex w-full max-w-[1272px] flex-col items-start justify-center gap-8 lg:gap-[clamp(3rem,8vh,7.5rem)]">
        <header className="relative flex w-full flex-col items-start gap-6">
          <div className="inline-flex items-center gap-4">
            <Badge className="inline-flex h-auto items-center gap-2 rounded-[99px] border border-[#0B3CFF4c] bg-[#0B3CFF33] px-2.5 py-1 pl-1.5 font-suisse-intl text-sm font-light leading-[17px] text-blue-200 shadow-none backdrop-blur-[2.5px]">
              <img className="h-5 w-5" src="/dubai/box.svg" alt="" aria-hidden="true" />
              Your Dubai company
            </Badge>
            <img
              className="hidden h-[6px] w-[114px] sm:block"
              src="/dubai/right-design.svg"
              alt=""
              aria-hidden="true"
            />
          </div>
          <h2 className="max-w-[1088px] font-space-grotesk text-[clamp(3.5rem,8.34vw,7.5rem)] font-medium leading-none tracking-[-0.06em]">
            <span className="text-white">What You Need to Get </span>
            <span className="text-[#acacac]">Started</span>
          </h2>
        </header>

        <div className="relative flex w-full flex-col items-start gap-10 lg:gap-[40px]">
          <div className="pointer-events-none absolute left-1/2 top-[-410px] -z-10 h-[710px] w-[min(731px,70vw)] -translate-x-1/2 rotate-90 rounded-[336px] bg-[radial-gradient(50%_50%_at_50%_50%,rgba(0,97,254,0.2)_0%,rgba(0,97,254,0)_68%)] blur-[12.5px]" />

          <div className="flex w-full flex-col items-start gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-12">
            <div className="flex w-full items-start sm:w-auto">
              <div className="flex h-24 w-full max-w-[490px] bg-white items-center overflow-hidden rounded-[170px]  px-7 sm:h-[140px] sm:w-[min(490px,calc(100vw_-_180px))] sm:px-[73px]">
                <span className="sr-only">What You Need to Get Started</span>
                <div
                  ref={marqueeRef}
                  aria-hidden="true"
                  className=" requirements-marquee-track whitespace-nowrap font-['Satoshi-Medium',Helvetica] text-[clamp(2.75rem,6.95vw,6.25rem)] font-medium leading-none tracking-[-0.02em] text-black"
                >
                  <span>What You Need to Get Started</span>
                  <span>What You Need to Get Started</span>
                </div>
              </div>
              <div
                className="hidden h-[140px] w-[140px] shrink-0 rotate-180 rounded-[170px] border-2 border-[#30363d] sm:block"
                aria-hidden="true"
              />
            </div>

            <p className="w-full max-w-[562px] font-suisse-intl text-base font-medium leading-[1.6] tracking-normal text-[#a0a4b3] sm:text-lg sm:leading-7">
              If you are setting up the company as an individual, we just need a
              clear copy of your passport, a digital passport photo, and your
              address details. If you already live in the UAE, you will also
              need to share your Emirates ID and current visa page. However, if
              you are registering the company under an existing corporate
              entity, or if your business activity falls into a regulated
              industry, the authority will ask for extra legal paperwork and
              corporate certificates.
            </p>
          </div>

          <nav
            className="flex w-full flex-wrap items-center justify-start gap-3 lg:gap-2 min-[1536px]:gap-4"
            aria-label="Company setup requirements"
          >
            {requirements.map((requirement) => {
              const isSelected = selectedRequirements.includes(requirement);

              return (
                <Button
                  key={requirement}
                  type="button"
                  variant="ghost"
                  aria-pressed={isSelected}
                  onClick={() => toggleRequirement(requirement)}
                  className={`h-auto min-h-14 whitespace-normal rounded-[55px] bg-[#1a1a1a] px-5 py-3 font-suisse-intl text-[12.5px] font-medium leading-[18.1px] text-white transition-colors hover:bg-[#262626] sm:px-6 ${
                    isSelected ? "bg-[#0061FE33] hover:bg-[#0061FE44]" : ""
                  }`}
                >
                  <span className=" flex h-6 w-6 shrink-0 items-center justify-center rounded-2xl border border-white">
                    <svg
                      aria-hidden="true"
                      className="h-4 w-4"
                      viewBox="0 0 16 24"
                      fill="none"
                    >
                      <path
                        d="m2.66 12 3.2 3.2 7.48-7.2"
                        stroke="#ffffff"
                        strokeWidth="1.33"
                      />
                    </svg>
                  </span>
                  {requirement}
                </Button>
              );
            })}
          </nav>
        </div>
      </div>
    </section>
  );
};
